import { createContext, useState, useCallback } from 'react';
import { trackError } from '../utils';

type ToastType = 'success' | 'error' | 'info';

interface Toast {
  message: string;
  type: ToastType;
  autoDismiss: boolean;
  action?: {
    label: string;
    onClick: () => void;
  };
}

const ToastContext = createContext<{
  toast: Toast | null;
  showToast: (
    message: string,
    type?: ToastType,
    autoDismiss?: boolean,
    action?: { label: string; onClick: () => void },
    contentId?: string
  ) => void;
  hideToast: () => void;
}>({
  toast: null,
  showToast: () => {},
  hideToast: () => {},
});

const ToastProvider = ({ children }: { children: React.ReactNode }) => {
  const [toast, setToast] = useState<Toast | null>(null);

  const showToast = useCallback(
    (
      message: string,
      type: ToastType = 'info',
      autoDismiss = true,
      action?: { label: string; onClick: () => void },
      contentId?: string
    ) => {
      setToast({ message, type, autoDismiss, action });

      // Track error toasts for debugging
      if (type === 'error') {
        trackError(`Toast: ${message}`, contentId);
      }

      if (autoDismiss) {
        setTimeout(() => setToast(null), 3000);
      }
    },
    []
  );

  const hideToast = useCallback(() => {
    setToast(null);
  }, []);

  return (
    <ToastContext.Provider value={{ toast, showToast, hideToast }}>
      {children}
    </ToastContext.Provider>
  );
};

export { ToastContext, ToastProvider };
